import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useSystemLogs } from '@/hooks/useSystemLogs';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ErrorMetricsCards } from '@/components/systemHealth/ErrorMetricsCards';
import { ErrorLogsTable } from '@/components/systemHealth/ErrorLogsTable';
import { DatabaseHealthCard } from '@/components/systemHealth/DatabaseHealthCard';
import { FinancialIntegrityCard } from '@/components/systemHealth/FinancialIntegrityCard';
import { TenantSyncStatus } from '@/components/systemHealth/TenantSyncStatus';
import { AuthLogsCard } from '@/components/systemHealth/AuthLogsCard';
import { MetroHealthCard } from '@/components/systemHealth/MetroHealthCard';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function SystemHealth() {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const { tenantId, hasTenant } = useTenantContext();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { data: logs = [], isLoading: logsLoading, refetch: refetchLogs } = useSystemLogs();

  // Repair null UUID invites
  const { data: repairData, refetch: refetchRepair } = useQuery({
    queryKey: ['repair-invites'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('repair_null_uuid_invites');
      if (error) throw error;
      return data;
    },
    enabled: false,
  });

  const handleRepairInvites = async () => {
    try {
      setIsRefreshing(true);
      const { data } = await refetchRepair();
      if (data && data.length > 0) {
        const repaired = data[0];
        toast.success(`Repaired ${repaired.repaired_count} invites`);
      } else {
        toast.info('No invites needed repair');
      }
    } catch (error: any) {
      toast.error(`Repair failed: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshAll = () => {
    setIsRefreshing(true);
    refetchLogs();
    queryClient.invalidateQueries({ queryKey: ['financial-integrity-checks'] });
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('Data refreshed');
    }, 1000);
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              Only administrators can access the System Health page.
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Activity className="h-8 w-8" />
              System Health
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor system status, errors, and data integrity
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleRefreshAll}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="errors">Error Logs</TabsTrigger>
            <TabsTrigger value="database">Database Health</TabsTrigger>
            <TabsTrigger value="financial">Financial Integrity</TabsTrigger>
            <TabsTrigger value="auth">Auth Logs</TabsTrigger>
            <TabsTrigger value="repair">Repair Tools</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <ErrorMetricsCards logs={logs} />
            
            <div className="grid gap-4 md:grid-cols-2">
              <TenantSyncStatus />
              <DatabaseHealthCard />
            </div>

            <FinancialIntegrityCard />

            <MetroHealthCard />

            <Card>
              <CardHeader>
                <CardTitle>Current User Status</CardTitle>
                <CardDescription>Your authentication and tenant context</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="font-semibold">User ID:</div>
                  <div className="font-mono text-xs">{user?.id}</div>

                  <div className="font-semibold">Email:</div>
                  <div>{user?.email}</div>

                  <div className="font-semibold">Tenant ID:</div>
                  <div className="font-mono text-xs">{tenantId || 'None'}</div>

                  <div className="font-semibold">Has Valid Tenant:</div>
                  <div>
                    {hasTenant ? (
                      <Badge variant="success">
                        Yes
                      </Badge>
                    ) : (
                      <Badge variant="destructive">No</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Error Logs Tab */}
          <TabsContent value="errors" className="space-y-4">
            <ErrorLogsTable logs={logs} isLoading={logsLoading} />
          </TabsContent>

          {/* Database Health Tab */}
          <TabsContent value="database" className="space-y-4">
            <DatabaseHealthCard />
          </TabsContent>

          {/* Financial Integrity Tab */}
          <TabsContent value="financial" className="space-y-4">
            <FinancialIntegrityCard />
          </TabsContent>

          {/* Auth Logs Tab */}
          <TabsContent value="auth" className="space-y-4">
            <AuthLogsCard />
          </TabsContent>

          {/* Repair Tools Tab */}
          <TabsContent value="repair" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Data Repair Tools</CardTitle>
                <CardDescription>
                  Fix common data integrity issues
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-semibold">Repair Null UUID Invites</h3>
                  <p className="text-sm text-muted-foreground">
                    Fixes invites with invalid company_id (00000000-0000-0000-0000-000000000000)
                  </p>
                  <Button
                    onClick={handleRepairInvites}
                    disabled={isRefreshing}
                    variant="outline"
                  >
                    {isRefreshing ? 'Repairing...' : 'Run Repair'}
                  </Button>
                  {repairData && repairData.length > 0 && (
                    <Alert className="mt-2">
                      <AlertTitle>Repair Complete</AlertTitle>
                      <AlertDescription>
                        Repaired {repairData[0].repaired_count} invites
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
